import type { MenuDiet } from '@pulsify/shared/dto'
import { Plus, X } from 'lucide-react'
import { useId } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import {
  appendDish,
  type DishDraft,
  isPriceInputValid,
  patchDish,
  removeDish,
  toggleDiet,
} from '../lib/menu-editor-model'

const DIETS: readonly { diet: MenuDiet; label: () => string }[] = [
  { diet: 'vegetarian', label: m.menuDietVegetarian },
  { diet: 'vegan', label: m.menuDietVegan },
]

type DishPatch = Partial<Omit<DishDraft, 'key'>>

function DishRow({
  dish,
  onChange,
  onRemove,
}: {
  dish: DishDraft
  onChange: (patch: DishPatch) => void
  onRemove: () => void
}) {
  const id = useId()
  const priceValid = isPriceInputValid(dish.price)

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 py-3 sm:grid-cols-[minmax(0,1fr)_8rem_auto]">
      <div className="flex min-w-0 flex-col gap-2">
        <Input
          aria-label={m.menuEditorDishName()}
          value={dish.name}
          maxLength={120}
          placeholder={m.menuEditorDishNamePlaceholder()}
          onChange={(event) => onChange({ name: event.target.value })}
          className="font-medium"
        />
        <Input
          aria-label={m.menuEditorDishDescription()}
          value={dish.description}
          maxLength={300}
          placeholder={m.menuEditorDishDescriptionPlaceholder()}
          onChange={(event) => onChange({ description: event.target.value })}
        />
        <div className="flex flex-wrap gap-1.5">
          {DIETS.map(({ diet, label }) => {
            const pressed = dish.diets.includes(diet)
            return (
              <button
                key={diet}
                type="button"
                aria-pressed={pressed}
                onClick={() => onChange({ diets: toggleDiet(dish.diets, diet) })}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 font-medium text-xs transition-colors duration-150 ease-spring',
                  pressed
                    ? 'border-transparent bg-brand-subtle text-brand-subtle-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {label()}
              </button>
            )
          })}
        </div>
      </div>
      <div className="col-start-1 flex flex-col gap-1 sm:col-start-2 sm:row-start-1">
        <Input
          aria-label={m.menuEditorDishPrice()}
          aria-invalid={!priceValid || undefined}
          aria-describedby={priceValid ? undefined : `${id}-price`}
          inputMode="decimal"
          value={dish.price}
          placeholder={m.menuEditorDishPricePlaceholder()}
          onChange={(event) => onChange({ price: event.target.value })}
          className="text-right tabular-nums"
        />
        {!priceValid && (
          <p id={`${id}-price`} className="text-destructive text-xs">
            {m.menuEditorDishPriceInvalid()}
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={m.menuEditorRemoveItem({ name: dish.name.trim() || m.menuEditorUnnamedDish() })}
        onClick={onRemove}
        className="col-start-2 row-start-1 text-muted-foreground hover:text-destructive sm:col-start-3"
      >
        <X />
      </Button>
    </li>
  )
}

interface DishRowsProps {
  dishes: DishDraft[]
  limit: number
  /** False once the whole menu holds as many dishes as it may. */
  canAdd?: boolean
  onChange: (dishes: DishDraft[]) => void
}

export function DishRows({ dishes, limit, canAdd = true, onChange }: DishRowsProps) {
  return (
    <div className="flex flex-col">
      {dishes.length === 0 ? (
        <p className="py-4 text-muted-foreground text-sm">{m.menuEditorNoDishes()}</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {dishes.map((dish) => (
            <DishRow
              key={dish.key}
              dish={dish}
              onChange={(patch) => onChange(patchDish(dishes, dish.key, patch))}
              onRemove={() => onChange(removeDish(dishes, dish.key))}
            />
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!canAdd || dishes.length >= limit}
        onClick={() => onChange(appendDish(dishes, limit))}
        className="mt-2 w-full border-dashed"
      >
        <Plus />
        {m.menuEditorAddDish()}
      </Button>
    </div>
  )
}
